
// // frontend/src/components/ScheduleList.js

import { useTheme } from '../contexts/ThemeContext';
import { usePreferences } from '../contexts/PreferenceContext';
import {LucideView, LucideListOrdered} from 'lucide-react'
import Tippy from '@tippyjs/react';
import moment from 'moment-timezone';

function ScheduleList({
  schedules,
  conflicts,
  scheduleMap,
  currentEntity,
  selectedScheduleId,
  handleSelectSchedule,
  onSelectSchedule,
  setSelectedScheduleForPrioritise,
  setIsModalOpen,
  getScheduleSnapshot,
  createTippyTooltip,
  t,
}) {
  const { text, cn } = useTheme();
  const { entitySettings } = usePreferences();
  
  return (
    <ul className="space-y-2">
      {schedules.map((schedule) => {
        // Determine if the schedule is expired
        const entityTimeZone = entitySettings?.time_zone || currentEntity.time_zone || 'UTC';
        const scheduleEndDate = moment.tz(schedule.end_date, ['YYYY-MM-DD', moment.ISO_8601], entityTimeZone);
        const currentDate = moment.tz(entityTimeZone);
        const isExpired = scheduleEndDate.isBefore(currentDate, 'day');

        // Check for conflicts
        const hasConflicts = conflicts[schedule.id] && conflicts[schedule.id].length > 0;
        const isSelected = selectedScheduleId === schedule.id;

        return (
          <li
            key={schedule.id}
            className={cn(
              "flex items-center p-3 rounded-lg cursor-pointer transition-all duration-200 border",
              isExpired
                ? "bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400 border-gray-200 dark:border-gray-700"
                : hasConflicts
                ? "bg-red-50 dark:bg-red-900/20 border-red-200 dark:border-red-700 hover:bg-red-100 dark:hover:bg-red-900/30"
                : cn(
                    "border-gray-200 dark:border-gray-700",
                    "hover:bg-gray-50 dark:hover:bg-gray-700/50 hover:border-gray-300 dark:hover:border-gray-600"
                  ),
              isSelected && "ring-2 ring-blue-500 ring-opacity-50 border-blue-300 dark:border-blue-600"
            )}
            onClick={() => handleSelectSchedule(schedule.id)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                handleSelectSchedule(schedule.id);
              }
            }}
          >
            {/* Schedule Name with snapshot and Conflict Info */}
            <Tippy
              content={createTippyTooltip(schedule)}
              placement="bottom"
              theme="dark"
              arrow={true}
            >
              <div className="flex-grow">
                {/* Schedule Name with Conditional Line-Through */}
                <span className={cn(
                  "font-semibold",
                  isExpired 
                    ? "line-through text-gray-500 dark:text-gray-400" 
                    : text.primary
                )}>
                  {schedule.name}
                </span>

                {/* Snapshot Information */}
                <span className={cn("text-sm ml-2 cursor-pointer", text.muted)}>
                  {getScheduleSnapshot(schedule)}
                </span>

                {/* Conflicts Information */}
                {hasConflicts && (
                  <div className="mt-1">
                    <span className="text-sm text-red-600 dark:text-red-400">
                      {t('Conflicts with')}:{' '}
                      {conflicts[schedule.id]
                        .map((conflictId) => {
                          const conflictSchedule = scheduleMap[conflictId];
                          const priority =
                            conflictSchedule.priority !== undefined
                              ? conflictSchedule.priority
                              : 'N/A';
                          return conflictSchedule
                            ? `${conflictSchedule.name} {${priority}}`
                            : t('Unknown Schedule');
                        })
                        .join(', ')}
                    </span>
                  </div>
                )}
              </div>
            </Tippy>

            {/* Conditionally Render "View Details" and "Prioritise" Buttons */}
            {isSelected && (
              <div className="flex items-center gap-2 ml-4">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectSchedule(schedule);
                  }}
                  className={cn(
                    "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                    "bg-blue-600 hover:bg-blue-700 text-white shadow-sm"
                  )}
                  aria-label={t('View Details')}
                >                  
                  <LucideView size={16} />
                  {t('View Details')}
                </button>

                {/* Prioritise Button */}
                {hasConflicts && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedScheduleForPrioritise(schedule);
                      setIsModalOpen(true);
                    }}
                    className={cn(
                      "flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors",
                      "bg-green-600 hover:bg-green-700 text-white shadow-sm"
                    )}
                    aria-label={t('Prioritise')}
                  >
                    <LucideListOrdered size={16} />
                    {t('Prioritise')}
                  </button>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ul>
  );
}

export default ScheduleList;
